/**
 * LookupCombo — the XAF lookup control: a multi-COLUMN grid popup with a filter
 * box, used by any field whose value comes from a reference list.
 *
 * Two behaviours, one component:
 *   · single-select — click opens the grid popup; picking a row closes it and
 *     returns the WHOLE row (so the caller can fill dependent read-only fields,
 *     e.g. Client → Address + Phone, or Test → its TestMethod id).
 *   · CheckedComboBox (`multi`) — the popup is a checkbox list that STAYS OPEN
 *     while you tick, and the closed field shows the picks semicolon-joined
 *     ("Water; Solid"), exactly like the legacy XAF CheckedComboBox.
 *
 * Optionally renders the inline green "＋" quick-add — the host owns the dialog.
 *
 * PROMOTED to components/ on 2026-07-28: it began life inside Sample Registration
 * with a note to move it here "when a second form needs the same control", and
 * SDMS Result Entry (Test / QCBatchID combos) is that second form. The `sr-*`
 * class names are kept so Sample Registration's markup is unaffected; hosts that
 * need a different TRIGGER look override `.sr-combo-v` in their own scoped CSS
 * (see `.sdms-root .sr-combo-v`).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './LookupCombo.css';

/** Column captions + rows for the grid popup; column 0 is the VALUE.
 *  (Structurally identical to Sample Registration's `SrLookup`, so existing
 *  callers keep working without a cast.) */
export interface LookupData { cols: string[]; rows: string[][] }

const POPUP_WIDTH = 520;

export interface LookupComboProps {
  /** Column captions + rows; column 0 is the value. */
  lookup: LookupData;
  /** Single-select: the chosen value ('' = none).  Ignored when `multi`. */
  value?: string;
  /** CheckedComboBox: the ticked values. */
  values?: string[];
  multi?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Single-select pick — receives the FULL row so dependants can be filled. */
  onPick?: (row: string[]) => void;
  /** CheckedComboBox toggle — receives the next full selection. */
  onToggle?: (next: string[], toggled: string[]) => void;
  /** Renders the inline green ＋; called when it is clicked. */
  onNew?: () => void;
  newTitle?: string;
  /** Blocked reason — clicking warns instead of opening (e.g. "pick a matrix"). */
  blockedReason?: string;
  onBlocked?: (reason: string) => void;
  /** Extra class on the trigger (e.g. the missing-required flash). */
  className?: string;
  id?: string;
  /** Show the filter box above the grid. Default true; OG's SDMS ribbon combos
   *  have no filter, so they pass false. */
  showFilter?: boolean;
  /** Popup width in px (default 520) — widen it for grids with many columns. */
  popupWidth?: number;
  /** Per-column widths (number = px, or any CSS length), positionally aligned to
   *  `lookup.cols`; omitted entries size automatically. Mirrors OG setting explicit
   *  widths (e.g. the SDMS Test combo's Method column at 350). */
  colWidths?: (number | string)[];
  /** Let cell text wrap onto a second line instead of being clipped to one line
   *  (OG's `ItemStyle.Wrap = True` on the SDMS Test combo). */
  wrapCells?: boolean;
  /** Extra class on the portaled popup (`.sr-pick`) so a host can scope popup-only
   *  overrides — the popup renders at document.body, outside the host's subtree. */
  popupClassName?: string;
  /** CheckedComboBox only: show "Select all" / "Clear" quick actions in the popup
   *  footer. "Select all" ticks every row currently matching the filter; "Clear"
   *  empties the selection. Opt-in so existing multi combos are unchanged. */
  multiActions?: boolean;
}

export function LookupCombo({
  lookup, value = '', values = [], multi = false, disabled = false,
  placeholder = 'N/A', onPick, onToggle, onNew, newTitle,
  blockedReason, onBlocked, className, id,
  showFilter = true, popupWidth, colWidths, wrapCells = false, popupClassName, multiActions = false,
}: LookupComboProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  const width = popupWidth ?? POPUP_WIDTH;

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    // Clamp inside the viewport so the popup is never clipped (§18.6).
    setPos({
      top: Math.min(r.bottom + 4, Math.max(8, window.innerHeight - 340)),
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
    });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => place();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  useEffect(() => {
    if (open && showFilter) setTimeout(() => filterRef.current?.focus(), 20);
  }, [open, showFilter]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lookup.rows;
    return lookup.rows.filter(r => r.join(' ').toLowerCase().includes(q));
  }, [lookup.rows, query]);

  const toggleTrigger = () => {
    if (disabled) return;
    if (blockedReason) { onBlocked?.(blockedReason); return; }
    setQuery('');
    setOpen(o => !o);
  };

  const pick = (row: string[]) => {
    if (multi) {
      const v = row[0] ?? '';
      const next = values.includes(v) ? values.filter(x => x !== v) : [...values, v];
      onToggle?.(next, row);
      return;   // CheckedComboBox stays open
    }
    onPick?.(row);
    setOpen(false);
  };

  // CheckedComboBox quick actions (opt-in via `multiActions`).
  const filteredVals = rows.map(r => r[0] ?? '').filter(Boolean);
  const allFilteredSelected  = filteredVals.length > 0 && filteredVals.every(v => values.includes(v));
  const someFilteredSelected = filteredVals.some(v => values.includes(v));
  // Header checkbox: tick every filtered row, or untick them all if already all on.
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const remove = new Set(filteredVals);
      onToggle?.(values.filter(v => !remove.has(v)), filteredVals);
    } else {
      const all = new Set(values);
      for (const v of filteredVals) all.add(v);
      onToggle?.(Array.from(all), filteredVals);
    }
  };
  const clearAll = () => onToggle?.([], values);

  const label = multi ? values.join('; ') : value;

  return (
    <div className="sr-combo">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`sr-combo-v${className ? ` ${className}` : ''}`}
        disabled={disabled}
        onClick={toggleTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label
          ? (multi
            ? <span className="sr-combo-val">{label}</span>
            : <span className="sr-combo-tag">{label}</span>)
          : <span className="sr-combo-ph">{placeholder}</span>}
        {onNew && (
          <span
            className="sr-combo-new"
            role="button"
            tabIndex={-1}
            title={newTitle ?? 'New'}
            onClick={(e) => { e.stopPropagation(); onNew(); }}
          >＋</span>
        )}
        <span className="sr-combo-caret" aria-hidden="true" />
      </button>

      {open && pos && createPortal(
        <div
          className={`sr-pick${wrapCells ? ' sr-pick-wrapcells' : ''}${popupClassName ? ` ${popupClassName}` : ''}`}
          ref={popRef}
          style={{ top: pos.top, left: pos.left, width }}
          role="listbox"
        >
          {showFilter && (
            <div className="sr-pick-f">
              <input
                ref={filterRef}
                placeholder="Filter…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          )}
          <div className="sr-pick-wrap">
            <table className="sr-pick-table">
              {colWidths && (
                <colgroup>
                  {multi && <col style={{ width: 34 }} />}
                  {lookup.cols.map((c, i) => {
                    const w = colWidths[i];
                    return <col key={c} style={w === undefined ? undefined : { width: w }} />;
                  })}
                </colgroup>
              )}
              <thead>
                <tr>
                  {multi && (
                    <th className="sr-pick-cb">
                      {multiActions && (
                        <input
                          type="checkbox"
                          ref={el => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                          checked={allFilteredSelected}
                          onChange={toggleAllFiltered}
                          onClick={e => e.stopPropagation()}
                          aria-label="Select all"
                          title="Select all / deselect all"
                        />
                      )}
                    </th>
                  )}
                  {lookup.cols.map(c => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td className="sr-pick-empty" colSpan={lookup.cols.length + (multi ? 1 : 0)}>
                      No matches.
                    </td>
                  </tr>
                ) : rows.map((r, i) => {
                  const v = r[0] ?? '';
                  const on = multi ? values.includes(v) : v === value;
                  return (
                    <tr key={`${v}-${i}`} className={on ? 'on' : ''} onClick={() => pick(r)}>
                      {multi && (
                        <td className="sr-pick-cb">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => pick(r)}
                            onClick={e => e.stopPropagation()}
                            aria-label={v}
                          />
                        </td>
                      )}
                      {r.map((c, ci) => <td key={ci}>{c}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sr-pick-g">
            {multi && multiActions && (
              <span className="sr-pick-acts">
                <button type="button" onClick={clearAll}>Clear</button>
              </span>
            )}
            <span>
              {multi ? `${values.length} selected · ${rows.length} items` : `${rows.length} items`}
            </span>
            {onNew && (
              <button className="ca-btn" style={{ padding: '2px 9px', fontSize: 11 }}
                onClick={() => { setOpen(false); onNew(); }}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
